import { Injectable, signal, computed, inject, OnDestroy, NgZone, afterNextRender } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject, of, interval, Subscription } from 'rxjs';
import { map, catchError, tap, switchMap, filter, take } from 'rxjs/operators';
import { NavigationService } from './navigation.service';
import { jwtDecode } from 'jwt-decode';

import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  User,
  AuthState,
  JwtPayload,
  AuthErrorResponse,
} from '@app/types/auth.interface';
import { environment } from '../../environments/environment';
import { AUTH_CONFIG } from '@app/core/config/auth.config';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private http = inject(HttpClient);
  private navigationService = inject(NavigationService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  // ==================== SINCRONIZACIÓN ENTRE PESTAÑAS ====================

  /**
   * BroadcastChannel para coordinar refresh de tokens entre pestañas.
   * Evita que múltiples pestañas hagan refresh simultáneamente.
   */
  private authChannel: BroadcastChannel | null = null;

  /**
   * Lock para evitar refresh local mientras otra pestaña está haciendo refresh.
   */
  private refreshLock = false;

  /**
   * Referencia al listener de storage para poder removerlo en cleanup.
   */
  private storageListener: ((event: StorageEvent) => void) | null = null;

  // Signals para el estado de autenticación
  private _authState = signal<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    refreshToken: null,
    role: null,
  });

  // Computed values
  readonly authState = this._authState.asReadonly();
  readonly isAuthenticated = computed(() => this._authState().isAuthenticated);
  readonly currentUser = computed(() => this._authState().user);
  readonly userRole = computed(() => this._authState().user?.role.toLocaleLowerCase() || null);

  /**
   * BehaviorSubject para coordinar múltiples requests que necesitan refresh.
   * Evita hacer múltiples llamadas al endpoint /auth/refresh simultáneamente.
   */
  private refreshTokenInProgress$ = new BehaviorSubject<boolean>(false);
  private refreshTokenSubject$ = new BehaviorSubject<string | null>(null);

  /**
   * Timer para renovación proactiva de tokens
   */
  private tokenRefreshTimer$?: Subscription;

  private initialized = false;

  constructor() {
    // Inicializar sincronización entre pestañas (no hace HTTP calls, seguro en constructor)
    this.setupBroadcastChannel();
    this.setupStorageListener();

    // Restaurar estado desde localStorage de forma síncrona (sin HTTP)
    this.restoreAuthStateFromStorage();

    // Diferir operaciones que usan HttpClient al siguiente render para evitar
    // circular DI: AuthService -> HttpClient -> authInterceptor -> AuthService
    afterNextRender(() => {
      if (this.initialized) return;
      this.initialized = true;
      this.initializeAsync();
    });
  }

  ngOnDestroy(): void {
    // Limpiar subscripciones
    this.tokenRefreshTimer$?.unsubscribe();

    // Limpiar BroadcastChannel
    this.authChannel?.close();
    this.authChannel = null;

    // Limpiar listener de storage
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
  }

  // ==================== PUBLIC API ====================

  /**
   * Realiza el login del usuario y almacena los tokens
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    const http = this.http;
    return http.post<LoginResponse>(`${environment.yuloadApiUrl}${AUTH_CONFIG.LOGIN_ENDPOINT}`, credentials).pipe(
      tap((response) => {
        this.setTokens(response.token, response.refresh_token);
        // Fetch user context from API (users/me)
        this.loadCurrentUser().subscribe({
          next: () => {},
          error: () => this.logout(),
        });
      }),
      catchError((error) => {
        console.error('Login failed:', error);
        return throwError(() => new Error('Credenciales incorrectas'));
      }),
    );
  }

  /**
   * Cierra la sesión del usuario y limpia todos los datos
   */
  logout(): void {
    // Notificar a otras pestañas antes de limpiar
    this.authChannel?.postMessage({ type: 'LOGOUT' });

    // Detener el timer de renovación proactiva
    this.tokenRefreshTimer$?.unsubscribe();

    // Limpiar estado
    this._authState.set({
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
    });

    // Limpiar almacenamiento
    this.clearStorage();

    // Redirigir al login
    this.navigationService.navigate(['/login']);
  }

  /**
   * Obtiene el access token actual
   */
  getAccessToken(): string | null {
    // Primero intentar desde el estado en memoria
    const stateToken = this._authState().token;
    if (stateToken) {
      return stateToken;
    }

    // Si no está en memoria, intentar desde localStorage
    return localStorage.getItem(AUTH_CONFIG.ACCESS_TOKEN_KEY);
  }

  /**
   * Obtiene el refresh token actual
   */
  getRefreshToken(): string | null {
    // Primero intentar desde el estado en memoria
    const stateRefreshToken = this._authState().refreshToken;
    if (stateRefreshToken) {
      return stateRefreshToken;
    }

    // Si no está en memoria, intentar desde localStorage
    return localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  }

  /**
   * Almacena los tokens en el estado y en localStorage
   */
  setTokens(accessToken: string, refreshToken: string): void {
    // Actualizar estado
    const currentState = this._authState();
    this._authState.set({
      ...currentState,
      isAuthenticated: true,
      token: accessToken,
      refreshToken: refreshToken,
    });

    // Persistir en localStorage
    localStorage.setItem(AUTH_CONFIG.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(AUTH_CONFIG.REFRESH_TOKEN_KEY, refreshToken);

    // Notificar a observadores que hay un nuevo token
    this.refreshTokenSubject$.next(accessToken);
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isUserAuthenticated(): boolean {
    return this._authState().isAuthenticated && !!this.getAccessToken();
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(role: string): boolean {
    return this._authState().user?.role === role;
  }

  /**
   * Renueva el access token usando el refresh token.
   * Esta función coordina múltiples llamadas simultáneas para evitar múltiples refreshes.
   * También coordina entre pestañas usando BroadcastChannel.
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    const http = this.http;

    // Si otra pestaña está haciendo refresh, esperar sincronización
    if (this.refreshLock) {
      console.log('🔒 Another tab is refreshing, waiting for sync...');
      return this.refreshTokenSubject$.pipe(
        filter((token) => token !== null),
        take(1),
        map((token) => ({
          token: token!,
          refresh_token: this.getRefreshToken()!,
        })),
      );
    }

    // Si ya hay un refresh en progreso en esta pestaña, esperar a que termine
    if (this.refreshTokenInProgress$.value) {
      console.log('🔄 Token refresh already in progress, waiting...');
      return this.refreshTokenSubject$.pipe(
        filter((token) => token !== null),
        take(1),
        map((token) => ({
          token: token!,
          refresh_token: this.getRefreshToken()!,
        })),
      );
    }

    // Obtener refresh token
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      console.error('❌ No refresh token available');
      this.logout();
      return throwError(() => new Error(AUTH_CONFIG.ERROR_MESSAGES.NO_REFRESH_TOKEN));
    }

    // Resetear el subject para que requests concurrentes esperen al nuevo token
    this.refreshTokenSubject$.next(null);

    // Marcar que hay un refresh en progreso
    this.refreshTokenInProgress$.next(true);

    // Notificar a otras pestañas que estamos haciendo refresh
    this.authChannel?.postMessage({ type: 'REFRESH_START' });

    console.log('🔄 Starting token refresh...');

    return http
      .post<RefreshTokenResponse>(
        `${environment.yuloadApiUrl}${AUTH_CONFIG.REFRESH_ENDPOINT}`,
        { refresh_token: refreshToken } as RefreshTokenRequest,
        { headers: { 'Skip-Auth': 'true' } },
      )
      .pipe(
        tap((response) => {
          console.log('✅ Token refresh successful');
          this.setTokens(response.token, response.refresh_token);
          this.refreshTokenInProgress$.next(false);

          // Notificar a otras pestañas que el refresh terminó
          this.authChannel?.postMessage({ type: 'REFRESH_DONE' });

          // Refrescar contexto del usuario (no hacer logout si falla — los tokens ya son válidos)
          this.loadCurrentUser().subscribe({
            next: () => {},
            error: (err) => console.warn('Failed to load user profile after refresh:', err),
          });
        }),
        catchError((error) => {
          console.error('❌ Token refresh failed:', error);
          this.refreshTokenInProgress$.next(false);

          // Notificar a otras pestañas que el refresh terminó (aunque haya fallado)
          this.authChannel?.postMessage({ type: 'REFRESH_DONE' });

          this.logout();
          return throwError(() => new Error(AUTH_CONFIG.ERROR_MESSAGES.REFRESH_FAILED));
        }),
      );
  }

  /**
   * Carga la información del usuario actual desde la API
   */
  loadCurrentUser(): Observable<User> {
    const http = this.http;
    const url = `${environment.yuloadApiUrl}${AUTH_CONFIG.USER_ME_ENDPOINT}`;
    const token = this.getAccessToken();

    const headers: any = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['Skip-Auth'] = 'true'; // Skip interceptor to avoid circular dependency
    }

    return http.post<any>(url, {}, { headers }).pipe(
      map((resp) => {
        const user: User = {
          id: resp.id,
          username: resp.name || resp.email,
          email: resp.email,
          role: (resp.role || 'USER').toString().toLowerCase() as 'admin' | 'user',
        };

        // Actualizar estado con el usuario
        const current = this._authState();
        this._authState.set({
          isAuthenticated: true,
          user,
          token: current.token,
          refreshToken: current.refreshToken,
        });

        // Persistir usuario en localStorage
        localStorage.setItem(AUTH_CONFIG.USER_DATA_KEY, JSON.stringify(user));
        return user;
      }),
      catchError((err) => {
        console.error('Error fetching current user:', err);
        throw err;
      }),
    );
  }

  // ==================== MÉTODOS PRIVADOS ====================

  // -------------------- Sincronización entre pestañas --------------------

  /**
   * Configura el BroadcastChannel para coordinar refresh de tokens entre pestañas.
   * Los mensajes soportados son:
   * - REFRESH_START: Otra pestaña inició un refresh, bloquear refresh local
   * - REFRESH_DONE: Otra pestaña terminó el refresh, sincronizar tokens
   * - LOGOUT: Otra pestaña cerró sesión, hacer logout local
   */
  private setupBroadcastChannel(): void {
    try {
      this.authChannel = new BroadcastChannel('yupanel_auth');

      this.authChannel.onmessage = (event) => {
        // Ejecutar dentro de NgZone para que Angular detecte los cambios
        this.ngZone.run(() => {
          switch (event.data?.type) {
            case 'REFRESH_START':
              console.log('📡 Another tab started refresh, locking local refresh');
              this.refreshLock = true;
              break;

            case 'REFRESH_DONE':
              console.log('📡 Another tab finished refresh, syncing tokens');
              this.refreshLock = false;
              this.syncTokensFromStorage();
              break;

            case 'LOGOUT':
              console.log('📡 Another tab logged out, logging out locally');
              this.handleExternalLogout();
              break;
          }
        });
      };

      console.log('📡 BroadcastChannel initialized for cross-tab sync');
    } catch (error) {
      // BroadcastChannel no soportado (ej: Safari en modo privado)
      console.warn('⚠️ BroadcastChannel not supported, cross-tab sync disabled:', error);
    }
  }

  /**
   * Configura el listener de StorageEvent para detectar cambios de tokens
   * realizados por otras pestañas directamente en localStorage.
   */
  private setupStorageListener(): void {
    this.storageListener = (event: StorageEvent) => {
      // Solo nos interesa el access_token
      if (event.key !== AUTH_CONFIG.ACCESS_TOKEN_KEY) {
        return;
      }

      // Ejecutar dentro de NgZone para que Angular detecte los cambios
      this.ngZone.run(() => {
        if (!event.newValue) {
          // Token eliminado = logout en otra pestaña
          console.log('🔑 Token removed by another tab, logging out');
          this.handleExternalLogout();
        } else if (event.newValue !== this._authState().token) {
          // Token cambió = refresh en otra pestaña
          console.log('🔑 Token updated by another tab, syncing');
          this.syncTokensFromStorage();
        }
      });
    };

    window.addEventListener('storage', this.storageListener);
    console.log('🔑 Storage listener initialized for cross-tab sync');
  }

  /**
   * Sincroniza los tokens desde localStorage al estado en memoria.
   * Se llama cuando otra pestaña actualiza los tokens.
   */
  private syncTokensFromStorage(): void {
    const token = localStorage.getItem(AUTH_CONFIG.ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);

    if (token && refreshToken) {
      console.log('🔄 Syncing tokens from storage');

      this._authState.update((state) => ({
        ...state,
        token,
        refreshToken,
        isAuthenticated: true,
      }));

      // Notificar a observadores que hay un nuevo token
      this.refreshTokenSubject$.next(token);
    }
  }

  /**
   * Maneja el logout iniciado por otra pestaña.
   * Limpia el estado local sin modificar localStorage (ya está limpio)
   * y sin enviar broadcast (para evitar loops).
   */
  private handleExternalLogout(): void {
    console.log('🚪 Handling external logout');

    // Detener el timer de renovación proactiva
    this.tokenRefreshTimer$?.unsubscribe();

    // Limpiar estado
    this._authState.set({
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
    });

    // NO llamar clearStorage() - ya está limpio por la otra pestaña
    // NO enviar broadcast - para evitar loops

    // Redirigir al login si no estamos ya ahí
    if (!this.router.url.includes('/login')) {
      this.navigationService.navigate(['/login']);
    }
  }

  // -------------------- Inicialización y Storage --------------------

  /**
   * Restaura el estado de autenticación desde localStorage de forma síncrona.
   * NO hace HTTP calls — seguro para ejecutar en el constructor.
   */
  private restoreAuthStateFromStorage(): void {
    const token = localStorage.getItem(AUTH_CONFIG.ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);

    if (token && refreshToken && !this.isTokenExpired(token)) {
      // Token válido, restaurar estado sin HTTP
      const cachedUserData = localStorage.getItem(AUTH_CONFIG.USER_DATA_KEY);
      let user: User | null = null;
      if (cachedUserData) {
        try {
          user = JSON.parse(cachedUserData);
        } catch {
          // Ignorar datos corruptos, se cargará desde API después
        }
      }

      this._authState.set({
        isAuthenticated: true,
        user,
        token,
        refreshToken,
      });
    }
  }

  /**
   * Inicialización asíncrona que requiere HttpClient.
   * Se ejecuta después del primer render para evitar circular DI.
   */
  private initializeAsync(): void {
    const token = localStorage.getItem(AUTH_CONFIG.ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);

    if (token && refreshToken) {
      if (this.isTokenExpired(token)) {
        console.log('⚠️ Access token is expired, attempting refresh...');
        this.refreshToken().subscribe({
          next: () => {
            console.log('✅ Token refreshed on startup');
            this.loadCurrentUser().subscribe();
          },
          error: () => {
            console.log('❌ Token refresh failed on startup, logging out');
            this.logout();
          },
        });
      } else {
        // Token válido, cargar información del usuario desde API
        this.loadCurrentUser().subscribe({
          next: () => console.log('✅ Auth state restored successfully'),
          error: (err) => {
            console.warn('Failed to load current user, using localStorage cache:', err);
          },
        });
      }
    }

    // Iniciar timer de renovación proactiva
    if (AUTH_CONFIG.PROACTIVE_REFRESH_ENABLED) {
      this.startProactiveTokenRefresh();
    }
  }

  /**
   * Limpia todos los datos de autenticación del almacenamiento
   */
  private clearStorage(): void {
    localStorage.removeItem(AUTH_CONFIG.ACCESS_TOKEN_KEY);
    localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_CONFIG.USER_DATA_KEY);
  }

  /**
   * Decodifica un JWT y retorna su payload
   */
  private decodeJwt(token: string): JwtPayload | null {
    try {
      return jwtDecode<JwtPayload>(token);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  /**
   * Verifica si un token JWT está expirado
   */
  private isTokenExpired(token: string): boolean {
    const payload = this.decodeJwt(token);
    if (!payload || !payload.exp) {
      return true;
    }

    // exp está en segundos, Date.now() está en milisegundos
    const expirationTime = payload.exp * 1000;
    const now = Date.now();

    return now >= expirationTime;
  }

  /**
   * Obtiene el tiempo restante hasta la expiración del token en milisegundos
   */
  private getTimeUntilExpiration(token: string): number {
    const payload = this.decodeJwt(token);
    if (!payload || !payload.exp) {
      return 0;
    }

    const expirationTime = payload.exp * 1000;
    const now = Date.now();

    return Math.max(0, expirationTime - now);
  }

  /**
   * Inicia el timer de renovación proactiva de tokens.
   * Verifica periódicamente si el token está cerca de expirar y lo renueva automáticamente.
   */
  private startProactiveTokenRefresh(): void {
    console.log('⏰ Starting proactive token refresh timer');

    this.tokenRefreshTimer$ = interval(AUTH_CONFIG.CHECK_INTERVAL_MS)
      .pipe(filter(() => this.isUserAuthenticated()))
      .subscribe(() => {
        const token = this.getAccessToken();

        if (!token) {
          return;
        }

        const timeUntilExpiration = this.getTimeUntilExpiration(token);
        const shouldRefresh =
          timeUntilExpiration > 0 && timeUntilExpiration <= AUTH_CONFIG.PROACTIVE_REFRESH_THRESHOLD_MS;

        if (shouldRefresh) {
          console.log(`⚠️ Token expires in ${Math.floor(timeUntilExpiration / 1000)}s, refreshing proactively...`);

          this.refreshToken().subscribe({
            next: () => console.log('✅ Proactive token refresh successful'),
            error: (err) => console.error('❌ Proactive token refresh failed:', err),
          });
        }
      });
  }

  // ==================== MÉTODO LEGACY (mantener compatibilidad) ====================

  /**
   * @deprecated Use refreshToken() instead
   */
  refreshAuthToken(): Observable<LoginResponse> {
    return this.refreshToken().pipe(
      map((response) => ({
        token: response.token,
        refresh_token: response.refresh_token,
      })),
    );
  }

  /**
   * @deprecated Use getAccessToken() instead
   */
  getAuthToken(): string | null {
    return this.getAccessToken();
  }
}
